export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const min = parseInt(minutes, 10);
  
  const period = hour >= 12 ? 'pm' : 'am';
  let displayHour = hour;
  
  if (hour === 0) {
    displayHour = 12;
  } else if (hour > 12) {
    displayHour = hour - 12;
  }
  
  if (min === 0) {
    return `${displayHour}${period}`;
  }
  return `${displayHour}:${min.toString().padStart(2, '0')}${period}`;
};

export const formatPlanName = (plan, isAccountVersion = null) => {
  const pricingData = plan.pricing_data || {};
  const pricingType = plan.pricing_type || '';
  
  const titleCaseName = toTitleCase(pricingType);
  
  const shouldFormatAsAccount = isAccountVersion !== null 
    ? isAccountVersion 
    : (pricingData.is_account === true && pricingData.is_individual !== true);
  
  if (shouldFormatAsAccount) {
    return `Group Plan - ${titleCaseName}`;
  }
  
  return titleCaseName;
};

export const formatAccessTime = (allowedTimeConfig) => {
  if (!allowedTimeConfig || !Array.isArray(allowedTimeConfig) || allowedTimeConfig.length === 0) {
    return { type: 'text', content: '24x7 access' };
  }

  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  const weekends = ['SATURDAY', 'SUNDAY'];
  const dayNames = {
    'MONDAY': 'Monday',
    'TUESDAY': 'Tuesday',
    'WEDNESDAY': 'Wednesday',
    'THURSDAY': 'Thursday',
    'FRIDAY': 'Friday',
    'SATURDAY': 'Saturday',
    'SUNDAY': 'Sunday'
  };

  const timeRanges = allowedTimeConfig.map(config => {
    const restrictedTimes = config.restricted_times || [];
    if (restrictedTimes.length === 0) return null;
    
    const timeRange = restrictedTimes[0];
    return {
      day: config.day,
      start: timeRange.start,
      end: timeRange.end,
      timeKey: `${timeRange.start}-${timeRange.end}`
    };
  }).filter(Boolean);

  if (timeRanges.length === 0) {
    return { type: 'text', content: '24x7 access' };
  }

  const timeGroups = {};
  timeRanges.forEach(range => {
    if (!timeGroups[range.timeKey]) {
      timeGroups[range.timeKey] = [];
    }
    timeGroups[range.timeKey].push(range.day);
  });

  const uniqueTimeKeys = Object.keys(timeGroups);
  if (uniqueTimeKeys.length === 1) {
    // All days have the same time
    const timeKey = uniqueTimeKeys[0];
    const days = timeGroups[timeKey];
    const firstRange = timeRanges.find(r => r.timeKey === timeKey);
    const timeStr = `${formatTime(firstRange.start)} – ${formatTime(firstRange.end)}`;
    
    const hasWeekdays = days.some(day => weekdays.includes(day));
    const hasWeekends = days.some(day => weekends.includes(day));
    
    let content = timeStr;
    if (hasWeekdays && hasWeekends) {
      content = `${timeStr} weekdays + weekends`;
    } else if (hasWeekdays) {
      content = `${timeStr} weekdays only`;
    } else if (hasWeekends) {
      content = `${timeStr} weekends only`;
    }
    
    return { type: 'text', content };
  } else {
    const sortedRanges = timeRanges.sort((a, b) => {
      const allDays = [...weekdays, ...weekends];
      return allDays.indexOf(a.day) - allDays.indexOf(b.day);
    });
    
    return {
      type: 'multiline',
      content: sortedRanges.map(range => {
        const timeStr = `${formatTime(range.start)} – ${formatTime(range.end)}`;
        return `${timeStr} ${dayNames[range.day]}`;
      })
    };
  }
};

export const expandPlans = (plans) => {
  const expandedPlans = [];
  
  plans.forEach(plan => {
    const pricingData = plan.pricing_data || {};
    const isAccount = pricingData.is_account === true;
    const isIndividual = pricingData.is_individual === true;
    
    if (isAccount && isIndividual) {
      expandedPlans.push({
        ...plan,
        id: `${plan.id}-account`,
        originalId: plan.id,
        displayName: formatPlanName(plan, true),
        subscriptionType: 'account'
      });
      
      expandedPlans.push({
        ...plan,
        id: `${plan.id}-individual`,
        originalId: plan.id,
        displayName: formatPlanName(plan, false),
        subscriptionType: 'individual'
      });
    } else if (isAccount) {
      expandedPlans.push({
        ...plan,
        displayName: formatPlanName(plan, true),
        subscriptionType: 'account'
      });
    } else if (isIndividual) {
      expandedPlans.push({
        ...plan,
        displayName: formatPlanName(plan, false),
        subscriptionType: 'individual'
      });
    }
  });
  
  return expandedPlans;
};

export const applyVisibilityFilters = (pricingList, visible, showTemplate) => {
  let filteredList = pricingList || [];

  if (showTemplate) {
    const templateName = showTemplate.replaceAll('_', ' ').toLowerCase();
    filteredList = filteredList.filter(
      (item) =>
        item.pricing_type &&
        item.pricing_type.toLowerCase() === templateName
    );
  } else if (visible === 'false') {
    filteredList = filteredList.filter(
      (item) =>
        item.pricing_data &&
        item.pricing_data.hasOwnProperty('visible') &&
        item.pricing_data.visible === false
    );
  } else {
    filteredList = filteredList.filter(
      (item) =>
        item.pricing_data &&
        (!item.pricing_data.hasOwnProperty('visible') ||
          item.pricing_data.visible)
    );
  }
  return filteredList.filter((item) => item.status === 'active');
};

export const getPlanDescription = (pricingData) => {
  if (!pricingData || typeof pricingData !== 'object') {
    return '';
  }
  return pricingData.description || '';
};

export const convertValidity = (item) => {
  if (!item || !item.year || !item.month) {
    return null;
  }
  
  const moment = require('moment');
  
  let start, end;
  let year = item.year;
  let month = item.month;
  month = month < 10 ? '0' + month : month;
  start = `${year}-${month}-01 00:00:00`;
  
  const lastDay = moment([year, item.month - 1]).endOf('month').format('DD');
  end = `${year}-${month}-${lastDay} 23:59:59`;
  
  return { start, end };
};

// ============ Form Validation Helpers ============

export const nonSpecialCharacterForValue = (value) => {
  if (!value) return false;
  return /[!@#$%^&*()\-_=+\[\]{}|;:'",<.>/?]/g.test(value);
};

export const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// ============ Month Picker Helpers ============

export const PICKER_LANG = {
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  from: 'From',
  to: 'To'
};

export const getMonthPickerLimits = (parkingId) => {
  const moment = require('moment');
  const { getYearMonth } = require('../../helper');
  
  const min = parkingId === 6029 
    ? getYearMonth({ year: 2025, month: 7 }) 
    : getYearMonth();
  
  const max = { 
    year: parseInt(moment().add(11, 'months').format('YYYY')), 
    month: parseInt(moment().add(11, 'months').format('M')) 
  };
  
  return { min, max };
};

export const formatMonthYear = (validity) => {
  if (validity && validity.year && validity.month) {
    return PICKER_LANG.months[validity.month - 1] + '. ' + validity.year;
  }
  return '?';
};

// ============ SVG Icon Components (as JSX strings for reuse) ============

export const ICONS = {
  remove: {
    width: 12,
    height: 12,
    paths: ['M18 6 6 18', 'm6 6 12 12']
  },
  add: {
    width: 16,
    height: 16,
    paths: ['M5 12h14', 'M12 5v14']
  },
  addSmall: {
    width: 12,
    height: 12,
    paths: ['M5 12h14', 'M12 5v14']
  }
};

